import React, { useRef } from "react";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import toast from "react-hot-toast";

import { Vector3 } from "three";

import ControlPanelButton from "./control-panel-button";

interface UploadButtonProps {
  /** Whether the upload button should be disabled */
  disabled: boolean;
  /** Callback function triggered when points are successfully uploaded and parsed */
  onPointsUploaded: (points: Vector3[]) => void;
}

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const UploadPointsButton: React.FC<UploadButtonProps> = ({
  disabled,
  onPointsUploaded,
}) => {

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File is too large. Maximum allowed size is ${MAX_FILE_SIZE_MB} MB.`)
      return;
    }

    // Optionally check file type
    if (file.type !== "application/json" && !file.name.endsWith(".json")) {
      toast.error("Please upload a valid JSON file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      try {
        const { points } = JSON.parse(text);
        if (!points) {
          throw new Error("No points found in uploaded file");
        }

        const vectorPoints: Vector3[] = points.map(
          (point: any) => new Vector3(point.x, point.y, point.z)
        );
        onPointsUploaded(vectorPoints);

      } catch (err) {
        console.error("Failed to parse points from uploaded file:", err);
        toast.error("Failed to parse points from uploaded file.");
      }
    };
    reader.readAsText(file);
  };

  return (
    <ControlPanelButton
      disabled={disabled}
      icon={<FileUploadIcon />}
      onClick={handleButtonClick}
      activeTooltip="Upload Points"
      inactiveTooltip="Cannot Upload Points"
      tooltipPlacement="right"
    >
      <input
        type="file"
        accept={".json"}
        ref={fileInputRef}
        onChange={handleFileChange}
        aria-hidden={true}
        style={{ display: "none" }}
      />
    </ControlPanelButton>
  );
};

export default UploadPointsButton;